import * as pushService from 'services/pushService'

// Browser-side helpers for Web Push (VAPID). The API side lives in
// services/pushService.js; the service worker handlers in service-worker.js.

export const PUSH_PERMISSION = {
  UNSUPPORTED: 'unsupported',
  DEFAULT: 'default',
  GRANTED: 'granted',
  DENIED: 'denied'
}

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

export const isStandalone = () =>
  (typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)')?.matches) ||
  window.navigator?.standalone === true

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const getPermissionState = () =>
  isPushSupported() ? Notification.permission : PUSH_PERMISSION.UNSUPPORTED

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const getRegistration = async () => {
  if (!('serviceWorker' in navigator)) return null
  const existing = await navigator.serviceWorker.getRegistration()
  if (existing) return existing
  // In production the SW registers on window load; give it a moment.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise(resolve => setTimeout(() => resolve(null), 4000))
  ])
}

const serializeSubscription = subscription => {
  const json = subscription.toJSON()
  const encodings = window.PushManager?.supportedContentEncodings || ['aesgcm']
  return {
    endpoint: json.endpoint,
    keys: json.keys,
    contentEncoding: encodings[0] || 'aesgcm'
  }
}

export const getCurrentSubscription = async () => {
  if (!isPushSupported()) return null
  const registration = await getRegistration()
  if (!registration) return null
  return registration.pushManager.getSubscription()
}

export class PushError extends Error {
  constructor (code, message) {
    super(message || code)
    this.code = code
  }
}

/**
 * Ask for permission (must be called from a user gesture), subscribe the
 * browser and persist the subscription for the current user on the API.
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) throw new PushError('unsupported')

  const permission = await Notification.requestPermission()
  if (permission !== PUSH_PERMISSION.GRANTED) {
    return { status: permission, subscription: null }
  }

  const registration = await getRegistration()
  if (!registration) throw new PushError('no-service-worker')

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    const result = await pushService.fetchPushPublicKey()
    const publicKey = result?.data?.data?.publicKey
    if (!publicKey) throw new PushError('no-public-key')

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    })
  }

  await pushService.storePushSubscription(serializeSubscription(subscription))
  return { status: PUSH_PERMISSION.GRANTED, subscription }
}

/**
 * Remove the subscription on the API and in the browser.
 */
export const unsubscribeFromPush = async () => {
  const subscription = await getCurrentSubscription()
  if (!subscription) return
  try {
    await pushService.deletePushSubscription(subscription.endpoint)
  } catch (error) {
    console.warn('Failed to delete push subscription on the API:', error)
  }
  await subscription.unsubscribe()
}

/**
 * After login: if this browser already holds a subscription, make sure the
 * API links it to the user who just signed in. Silent no-op otherwise.
 */
export const syncPushSubscription = async () => {
  try {
    if (!isPushSupported() || Notification.permission !== PUSH_PERMISSION.GRANTED) return
    const subscription = await getCurrentSubscription()
    if (!subscription) return
    await pushService.storePushSubscription(serializeSubscription(subscription))
  } catch (error) {
    console.warn('Failed to sync push subscription:', error)
  }
}

/**
 * Before logout: detach this browser's subscription from the user so a
 * shared device stops receiving their notifications. The browser keeps the
 * subscription; the next user to sign in re-attaches it via sync.
 */
export const detachPushSubscription = async () => {
  try {
    const subscription = await getCurrentSubscription()
    if (!subscription) return
    await pushService.deletePushSubscription(subscription.endpoint)
  } catch (error) {
    console.warn('Failed to detach push subscription:', error)
  }
}

export const persistSubscriptionFromWorker = async subscription => {
  try {
    if (!subscription?.endpoint) return
    const encodings = window.PushManager?.supportedContentEncodings || ['aesgcm']
    await pushService.storePushSubscription({
      endpoint: subscription.endpoint,
      keys: subscription.keys,
      contentEncoding: encodings[0] || 'aesgcm'
    })
  } catch (error) {
    console.warn('Failed to persist renewed push subscription:', error)
  }
}
